/**
 * OneTrust Consent Management Utilities
 *
 * These utilities help check user consent status and manage analytics based on OneTrust cookie preferences.
 *
 * OneTrust Cookie Categories:
 * - C0001: Strictly Necessary Cookies (always active)
 * - C0002: Performance Cookies (analytics, monitoring)
 * - C0003: Functional Cookies
 * - C0004: Targeting Cookies
 */

declare global {
  interface Window {
    OneTrust?: {
      IsAlertBoxClosed: () => boolean
      GetDomainData: () => any
      AllowAll: () => void
      RejectAll: () => void
    }
    OnetrustActiveGroups?: string
    OptanonWrapper?: () => void
    OptanonWrapperCount?: number
  }
}

/**
 * OneTrust Cookie Categories
 */
export enum OneTrustCategory {
  /** Strictly Necessary Cookies - Always active */
  STRICTLY_NECESSARY = 'C0001',
  /** Performance/Analytics Cookies */
  PERFORMANCE = 'C0002',
  /** Functional Cookies */
  FUNCTIONAL = 'C0003',
  /** Targeting/Advertising Cookies */
  TARGETING = 'C0004',
}

/**
 * Get active cookie groups as a comma-separated string
 * Example: ",C0001,C0002," means Strictly Necessary and Performance are active
 */
export const getActiveGroups = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }
  return window.OnetrustActiveGroups || ''
}

/**
 * Check if a specific cookie category is consented to
 */
export const hasConsentForCategory = (category: OneTrustCategory): boolean => {
  const activeGroups = getActiveGroups()

  // Strictly necessary cookies are always active
  if (category === OneTrustCategory.STRICTLY_NECESSARY) {
    return true
  }

  // Check if the category is in the active groups string
  // Format is ",C0001,C0002," so we check for ",{category},"
  return activeGroups.includes(`,${category},`)
}

/**
 * Register a callback to be executed when OneTrust is ready
 * This wraps the global OptanonWrapper function
 */
export const onOneTrustReady = (callback: () => void): void => {
  if (typeof window === 'undefined') {
    return
  }

  const existingWrapper = window.OptanonWrapper
  const wrapperCount = (window.OptanonWrapperCount || 0) + 1
  window.OptanonWrapperCount = wrapperCount

  window.OptanonWrapper = function () {
    // Call existing wrapper if it exists
    if (existingWrapper && typeof existingWrapper === 'function') {
      existingWrapper()
    }

    // Call the new callback
    try {
      callback()
    } catch (error) {
      console.error('[OneTrust] Error in consent callback:', error)
    }
  }
}

/**
 * Get all active categories
 */
export const getActiveCategories = (): OneTrustCategory[] => {
  const activeGroups = getActiveGroups()
  const categories: OneTrustCategory[] = []

  Object.values(OneTrustCategory).forEach(category => {
    if (hasConsentForCategory(category)) {
      categories.push(category)
    }
  })

  return categories
}

export default {
  getActiveGroups,
  hasConsentForCategory,
  onOneTrustReady,
  getActiveCategories,
}
